import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { deleteDB, IDBPDatabase, openDB } from 'idb';
import { defer, Observable, of } from 'rxjs';
import { map, retry, startWith } from 'rxjs/operators';
import { ResolveApiToResponseLocally } from 'src/app/utils/resolve-api-to-response';
import { environment } from 'src/environments/environment';

const DATABASE_NAME = 'vam-pwa';
const DATABASE_VERSION = 1;

type StoreData = {
  storeName: string;
  storeKey: string;
};

const data = [
  {
    storeName: 'cities',
    storeKey: 'city_id'
  },
  {
    storeName: 'door_types',
    storeKey: 'door_type_id'
  },
  {
    storeName: 'assessment_types',
    storeKey: 'assessment_type_id'
  },
  {
    storeName: 'body_types',
    storeKey: 'body_type_id'
  },
  {
    storeName: 'conditions',
    storeKey: 'condition_id'
  },
  {
    storeName: 'drive_types',
    storeKey: 'drive_type_id'
  },
  {
    storeName: 'engine_types',
    storeKey: 'engine_type_id'
  },
  {
    storeName: 'exterior_colors',
    storeKey: 'exterior_color_id'
  },
  {
    storeName: 'fuel_types',
    storeKey: 'fuel_type_id'
  },
  {
    storeName: 'gears',
    storeKey: 'gear_id'
  },
  {
    storeName: 'interior_colors',
    storeKey: 'interior_color_id'
  },
  {
    storeName: 'market_acceptability',
    storeKey: 'market_acceptabilitie_id'
  },
  {
    storeName: 'max_speeds',
    storeKey: 'max_speed_id'
  },
  {
    storeName: 'mileage_info',
    storeKey: 'mileage_info_id'
  },
  {
    storeName: 'mortgage',
    storeKey: 'mortage_id'
  },
  {
    storeName: 'options',
    storeKey: 'option_id'
  },
  {
    storeName: 'ownership',
    storeKey: 'ownership_id'
  },
  {
    storeName: 'services',
    storeKey: 'service_id'
  },
  {
    storeName: 'torques',
    storeKey: 'torque_id'
  },
  {
    storeName: 'transmission_types',
    storeKey: 'transmission_type_id'
  },
  {
    storeName: 'utility_types',
    storeKey: 'utility_type_id'
  },
  {
    storeName: 'vehicle_accelerations',
    storeKey: 'acceleration_id'
  },
  {
    storeName: 'vehicle_codes',
    storeKey: 'cod_id'
  },
  {
    storeName: 'vehicle_cylinders',
    storeKey: 'cyllinder_id'
  },
  {
    storeName: 'vehicle_engine_cc',
    storeKey: 'engine_cc_id'
  },
  {
    storeName: 'vehicle_estimara',
    storeKey: 'estimara_id'
  },
  {
    storeName: 'vehicle_flags',
    storeKey: 'flag_id'
  },
  {
    storeName: 'vehicle_make',
    storeKey: 'make_id'
  },
  {
    storeName: 'vehicle_origin',
    storeKey: 'vehicle_origin_id'
  },
  {
    storeName: 'vehicle_powers',
    storeKey: 'power_id'
  },
  {
    storeName: 'vehicle_seats',
    storeKey: 'seat_id'
  },
  {
    storeName: 'vehicle_types',
    storeKey: 'type_id'
  },
  {
    storeName: 'vehicle_upholsteries',
    storeKey: 'uphoistery_id'
  },
  {
    storeName: 'vehicle_warranty',
    storeKey: 'warranty_id'
  },
  {
    storeName: 'vehicle_wheel_base',
    storeKey: 'wheel_base_id'
  },
  {
    storeName: 'estimator',
    storeKey: 'id'
  },
  {
    storeName: 'part_condition',
    storeKey: 'body_type_id'
  },
  {
    storeName: 'gallery',
    storeKey: 'gallery_id'
  },
  {
    storeName: 'VAM-PWA-API-CALLS',
    storeKey: 'vin'
  },
  {
    storeName: 'image-store',
    storeKey: 'id'
  }
] as const;

type ValidStoreNames = (typeof data)[number]['storeName'];
type ValidStoreKeys = (typeof data)[number]['storeKey'];

@Injectable({
  providedIn: 'root'
})
export class IndexedDbService {
  db: IDBPDatabase<unknown>;
  private readonly PWA_LOCAL_DATABASE_VERSION_LOCALSTORAGE_KEY = 'pwa-local-database-version';

  constructor(private readonly _httpClient: HttpClient) {
    // this.setupDatabase();
  }

  async setupDatabase() {
    this.db = await openDB(DATABASE_NAME, DATABASE_VERSION, {
      upgrade(database, oldVersion, newVersion, transaction, event) {
        for (let i = 0; i < data.length; i++) {
          database.createObjectStore(data[i].storeName, {
            keyPath: data[i].storeKey
          });
        }
      }
    });
  }

  async populateDatabaseForTheFirstTime(args: { data: any }): Promise<void> {
    console.log('inside populateDatabaseForTheFirstTime, received data:');
    console.log(args.data);

    for (let i = 0; i < data.length; i++) {
      this.db.clear(data[i].storeName);
    }

    console.log('object key length is', Object.keys(args.data).length);

    for (let [key, value] of Object.entries(args.data)) {
      // we receive a large object with a lot of keys but one of these keys will be called 'version' which maintains version number for the database.
      // we dont want this.
      if (key !== 'version' && this.db.objectStoreNames.contains(key)) {
        const storeName = key;
        const storeValue = value as Array<unknown>;
        const tx = this.db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        for (let i = 0; i < storeValue.length; i++) {
          await store.add(storeValue[i]);
        }
        await tx.done;
      } else {
        console.log('does not contain store for:', key);
      }
    }
  }

  populateIndexedDb(value: any) {
    console.log('populateIndexedDb');
    console.log({ value });

    const formattedVersion = parseFloat((value.version as number).toFixed(2));

    return defer(async () => {
      for (let i = 0; i < data.length; i++) {
        if (this.db.objectStoreNames.contains(data[i].storeName)) {
          await this.db.clear(data[i].storeName);
        }
      }

      for (let [k, v] of Object.entries(value)) {
        // we receive a large object with a lot of keys but one of these keys will be called 'version' which maintains version number for the database.
        // we dont want this.
        if (k !== 'version' && this.db.objectStoreNames.contains(k)) {
          const storeName = k;
          const storeValue = v as Array<unknown>;
          const tx = this.db.transaction(storeName, 'readwrite');
          const store = tx.objectStore(storeName);
          for (let i = 0; i < storeValue.length; i++) {
            await store.add(storeValue[i]);
          }
          await tx.done;
        } else {
          console.log('does not contain store for:', k);
        }
      }

      return { status: 'success', version: formattedVersion } as const;
    });
  }

  setupIndexedDbObjectStores() {
    return new Observable<{ status: 'success' | 'error'; database: IDBPDatabase<unknown> }>(
      (observer) => {
        openDB(DATABASE_NAME, DATABASE_VERSION, {
          upgrade(database, oldVersion, newVersion, transaction, event) {
            for (let i = 0; i < data.length; i++) {
              database.createObjectStore(data[i].storeName, {
                keyPath: data[i].storeKey
              });
            }
          }
        })
          .then((db) => {
            this.db = db;
            observer.next({ status: 'success', database: db });
            observer.complete();
          })
          .catch((error) => {
            observer.error(error);
          });
      }
    );
  }

  checkLatestDatabaseVersion() {
    const url = `${environment.api}/pwa/json-version`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    return this._httpClient.get<Record<string, unknown>>(url, { headers }).pipe(retry(3));
  }

  getPwaLocalDatabaseVersion() {
    const localDatabaseVersion = localStorage.getItem(
      this.PWA_LOCAL_DATABASE_VERSION_LOCALSTORAGE_KEY
    );
    if (localDatabaseVersion) return parseFloat(localDatabaseVersion);
    else {
      localStorage.setItem(this.PWA_LOCAL_DATABASE_VERSION_LOCALSTORAGE_KEY, String(0.0));
      return 0.0;
    }
  }

  setPwaLocalDatabaseVersion(version: number) {
    localStorage.setItem(this.PWA_LOCAL_DATABASE_VERSION_LOCALSTORAGE_KEY, String(version));
  }

  removePwaLocalDatabaseVersion() {
    localStorage.removeItem(this.PWA_LOCAL_DATABASE_VERSION_LOCALSTORAGE_KEY);
  }

  downloadLatestDatabase() {
    console.log('inside downloadLatestDatabase');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const url = `${environment.api}/pwa/getdata`;
    return this._httpClient.get(url, { headers });
  }

  clearDataBase() {
    console.log('inside clearDataBase');

    return defer(async () => {
      // console.log('ghgh:1');

      // try {
      //   await deleteDB(DATABASE_NAME);
      // } catch (error) {
      //   console.log(error);
      // }

      // console.log('ghgh:2');

      for (let i = 0; i < data.length; i++) {
        await this.db.clear(data[i].storeName);
      }
      this.removePwaLocalDatabaseVersion();
      return { status: 'success' } as const;
    });
  }
}
